import { useEffect, useState } from "react";
import { Form, Button, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { authFetch } from "@/lib/auth-fetch";

export interface EntityOption {
    id: number;
    name: string;
}

export interface TherapyFormValues {
    therapyType: string;
    clients: string[];
    therapist: string;
    helper1: string;
    helper2: string;
    horse: string;
    paidTherapist: boolean;
}

export interface TherapyFormOptions {
    clients: EntityOption[];
    therapists: EntityOption[];
    helpers: EntityOption[];
    horses: EntityOption[];
}

interface Props {
    options: TherapyFormOptions;
    values: TherapyFormValues;
    onChange: (values: TherapyFormValues) => void;
    therapistDisabled?: boolean;
}

export function useTherapyOptions(isEdit: boolean) {
    const [options, setOptions] = useState<TherapyFormOptions>({
        clients: [], therapists: [], helpers: [], horses: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        const q = !isEdit ? "?active=true" : "";
        const fetchJson = (url: string) => authFetch(url).then(r => {
            if (!r.ok) throw new Error(`Fehler beim Laden (${r.status})`);
            return r.json();
        });
        Promise.all([
            fetchJson(`/api/clients${q}`),
            fetchJson(`/api/therapists${q}`),
            fetchJson(`/api/helpers${q}`),
            fetchJson(`/api/horses${q}`),
        ])
            .then(([clients, therapists, helpers, horses]) => {
                if (!mounted) return;
                setOptions({ clients, therapists, helpers, horses });
            })
            .catch(e => { if (mounted) setError(e.message || "Fehler beim Laden"); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [isEdit]);

    return { options, loading, error };
}

export function TherapyFormFields({ options, values, onChange, therapistDisabled }: Props) {
    const handleClientChange = (index: number, value: string) => {
        const updated = [...values.clients];
        updated[index] = value;
        onChange({ ...values, clients: updated });
    };

    const addClientField = () => {
        if (values.clients.length < 4) {
            onChange({ ...values, clients: [...values.clients, ""] });
        }
    };

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Therapieart</Form.Label>
                <Form.Select value={values.therapyType} required onChange={e => onChange({ ...values, therapyType: e.target.value })}>
                    <option value="">Auswählen</option>
                    <option value="HP Einzel">HP Einzel</option>
                    <option value="HP Gruppe">HP Gruppe</option>
                    <option value="Hippotherapie">Hippotherapie</option>
                </Form.Select>
            </Form.Group>

            {values.clients.map((client, index) => (
                <Form.Group className="mb-3" key={index}>
                    <Form.Label>Klient {index + 1}</Form.Label>
                    <Form.Select value={client} onChange={e => handleClientChange(index, e.target.value)}>
                        <option value="">Auswählen</option>
                        {options.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Form.Select>
                </Form.Group>
            ))}
            {values.therapyType === "HP Gruppe" && values.clients.length < 4 && (
                <Button variant="secondary" size="sm" onClick={addClientField} className="mb-3">
                    Klient hinzufügen
                </Button>
            )}

            <Row>
                <Col md={8}>
                    <Form.Group className="mb-3">
                        <Form.Label>Fachkraft</Form.Label>
                        <Form.Select value={values.therapist} required disabled={therapistDisabled} onChange={e => onChange({ ...values, therapist: e.target.value })}>
                            <option value="">Auswählen</option>
                            {options.therapists.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="invisible">&#8203;</Form.Label>
                        <Form.Check
                            type="checkbox"
                            checked={values.paidTherapist}
                            onChange={e => onChange({ ...values, paidTherapist: e.target.checked })}
                            label={
                                <span className="d-inline-flex align-items-center gap-1">
                                    Fachkraft vergüten
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Deaktivieren, wenn die Fachkraft bereits anderweitig vergütet wird</Tooltip>}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" className="text-muted" style={{ cursor: "pointer" }}>
                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                                        </svg>
                                    </OverlayTrigger>
                                </span>
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Helfer 1 (optional)</Form.Label>
                        <Form.Select value={values.helper1} onChange={e => onChange({ ...values, helper1: e.target.value })}>
                            <option value="">Auswählen</option>
                            {options.helpers.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Helfer 2 (optional)</Form.Label>
                        <Form.Select value={values.helper2} onChange={e => onChange({ ...values, helper2: e.target.value })}>
                            <option value="">Auswählen</option>
                            {options.helpers.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Pferd</Form.Label>
                <Form.Select value={values.horse} onChange={e => onChange({ ...values, horse: e.target.value })}>
                    <option value="">Auswählen</option>
                    {options.horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </Form.Select>
            </Form.Group>
        </>
    );
}
